import React, { useState, useEffect } from 'react';
import API from '../utils/API';

export default function AdminApprovedEmails() {
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(true);

    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState('');
    const [editError, setEditError] = useState('');
    const [saving, setSaving] = useState({});

    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const [newEmail, setNewEmail] = useState('');
    const [addError, setAddError] = useState('');
    const [adding, setAdding] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await API.getApprovedEmailsList();
            setEmails(res.data);
        } catch (err) {
            console.error('Failed to load approved emails:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        const email = newEmail.trim().toLowerCase();
        if (!email) return;
        setAdding(true);
        setAddError('');
        try {
            await API.addApprovedEmailEntry({ approved_email: email });
            setNewEmail('');
            await load();
        } catch (err) {
            setAddError(err.response?.data?.msg || 'Failed to add email.');
        } finally {
            setAdding(false);
        }
    };

    const rowKey = (row) => `${row.source}_${row.id}`;

    const startEdit = (row) => {
        setEditingId(rowKey(row));
        setEditDraft(row.email);
        setEditError('');
        setConfirmDeleteId(null);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft('');
        setEditError('');
    };

    const saveEdit = async (row) => {
        const email = editDraft.trim().toLowerCase();
        if (!email) return;
        const key = rowKey(row);
        setSaving(s => ({ ...s, [key]: true }));
        setEditError('');
        try {
            if (row.source === 'beta') {
                await API.updateBetaEmail(row.id, { beta_email: email });
            } else {
                await API.updateApprovedEmailEntry(row.id, { approved_email: email });
            }
            cancelEdit();
            await load();
        } catch (err) {
            setEditError(err.response?.data?.msg || 'Failed to save.');
        } finally {
            setSaving(s => ({ ...s, [key]: false }));
        }
    };

    const confirmDelete = async (row) => {
        const key = rowKey(row);
        setSaving(s => ({ ...s, [`del_${key}`]: true }));
        try {
            if (row.source === 'beta') {
                await API.deleteBetaEmail(row.id);
            } else {
                await API.deleteApprovedEmailEntry(row.id);
            }
            setConfirmDeleteId(null);
            setEmails(prev => prev.filter(e => !(e.source === row.source && e.id === row.id)));
        } catch (err) {
            console.error('Failed to delete email:', err);
        } finally {
            setSaving(s => ({ ...s, [`del_${key}`]: false }));
        }
    };

    return (
        <div>
            <p className="text-muted small mb-3">
                These individual email addresses are eligible to create an account even if their domain isn't approved.
                Entries tagged <strong>Beta List</strong> come from the original beta access list; new additions here
                are saved separately as admin-approved emails.
            </p>

            <form className="ai-add-row" onSubmit={handleAdd}>
                <input
                    className="admin-search-input"
                    type="email"
                    placeholder="name@example.com"
                    value={newEmail}
                    onChange={e => { setNewEmail(e.target.value); setAddError(''); }}
                />
                <button className="awards-save-btn" type="submit" disabled={adding || !newEmail.trim()}>
                    {adding ? <i className="bi bi-arrow-repeat admin-spin" /> : 'Add'}
                </button>
            </form>
            {addError && <div className="ai-error mb-2">{addError}</div>}

            {loading ? (
                <div className="admin-loading">Loading…</div>
            ) : (
                <div className="admin-table-wrapper">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th className="admin-th-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {emails.length === 0 ? (
                                <tr><td colSpan={2} className="admin-empty">No approved emails found</td></tr>
                            ) : emails.map(row => (
                                <tr key={rowKey(row)}>
                                    <td data-label="Email">
                                        {editingId === rowKey(row) ? (
                                            <div className="admin-email-edit">
                                                <input
                                                    className="admin-email-input"
                                                    type="email"
                                                    value={editDraft}
                                                    onChange={e => { setEditDraft(e.target.value); setEditError(''); }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') saveEdit(row);
                                                        if (e.key === 'Escape') cancelEdit();
                                                    }}
                                                    autoFocus
                                                />
                                                <div className="admin-email-actions">
                                                    <button
                                                        className="admin-email-save"
                                                        onClick={() => saveEdit(row)}
                                                        disabled={saving[rowKey(row)]}
                                                        title="Save"
                                                    >
                                                        {saving[rowKey(row)]
                                                            ? <i className="bi bi-arrow-repeat admin-spin" />
                                                            : <i className="bi bi-check-lg" />}
                                                    </button>
                                                    <button className="admin-email-cancel" onClick={cancelEdit} title="Cancel">
                                                        <i className="bi bi-x-lg" />
                                                    </button>
                                                </div>
                                                {editError && <div className="admin-email-error">{editError}</div>}
                                            </div>
                                        ) : (
                                            <span className="admin-email-text" onClick={() => startEdit(row)} title="Click to edit">
                                                {row.email}
                                                {row.source === 'beta' && (
                                                    <span className="badge bg-light text-secondary border ms-2" style={{ fontWeight: 400, fontSize: '0.7rem' }}>
                                                        Beta List
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </td>
                                    <td className="admin-td-center" data-label="Actions">
                                        {confirmDeleteId === rowKey(row) ? (
                                            <div className="ai-delete-confirm">
                                                <span className="ai-delete-confirm-msg">Delete this email?</span>
                                                <button
                                                    className="awards-btn awards-btn--delete ai-action-btn"
                                                    onClick={() => confirmDelete(row)}
                                                    disabled={saving[`del_${rowKey(row)}`]}
                                                >
                                                    {saving[`del_${rowKey(row)}`]
                                                        ? <i className="bi bi-arrow-repeat admin-spin" />
                                                        : 'Yes, delete'}
                                                </button>
                                                <button
                                                    className="awards-btn ai-action-btn"
                                                    onClick={() => setConfirmDeleteId(null)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="ai-action-group">
                                                <button
                                                    className="awards-btn awards-btn--edit ai-action-btn"
                                                    onClick={() => startEdit(row)}
                                                    title="Edit"
                                                >
                                                    <i className="bi bi-pencil me-1" />Edit
                                                </button>
                                                <button
                                                    className="awards-btn awards-btn--delete ai-action-btn"
                                                    onClick={() => { setConfirmDeleteId(rowKey(row)); setEditingId(null); }}
                                                    title="Delete"
                                                >
                                                    <i className="bi bi-trash me-1" />Delete
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
